import { useState, useEffect } from 'react';
import { useMediaQuery } from '@mui/material';
import { ReactComponent as ProfilePicture } from '@Assets/blankProfile.svg';
import { IoClose as Close } from 'react-icons/io5';
import { MdPerson as PersonIcon, MdEmail as EmailIcon, MdWork as WorkIcon } from 'react-icons/md';
import UserAnalysisService from '@Helpers/URLHelpers/AnalyticsService/UserAnalysisService';
import ProfileUserCharts from '@UserDashboards/ProfileUserCharts/ProfileUserCharts';
import ProfileUserPerformance from '@UserDashboards/ProfileUserPerformance/ProfileUserPerformance';
import ProfileStatistics from '@UserDashboards/ProfileStatistics/ProfileStatistics';
import Grid from '@mui/material/Grid';
import Modal from '@mui/material/Modal';
import CircularProgress from '@mui/material/CircularProgress';
import UserInfoService from '@Helpers/URLHelpers/UserInfoService';
import ErrorHandler from '@Root/ErrorHandler';
import './ProfileView.scss';

export default function ProfileView(props) {    

    const [photo, setPhoto] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [userAnalysis, setUserAnalysis] = useState({});
    const [userAnalysisLoader, setUserAnalysisLoader] = useState(true);
    const [user, setUser] = useState({});
    const [loading, setLoading] = useState(true);
    const userInfoService = new UserInfoService();
    const errorHandler = new ErrorHandler();
    const userAnalysisService = new UserAnalysisService();
    const is0To1000 = useMediaQuery('(max-width: 1000px)');
    const is0To1200 = useMediaQuery('(max-width: 1200px)');
    const isMobile = useMediaQuery('(max-width: 768px)');
    const className = 'profileView';

    useEffect(() => {
        if (props.profileId) {
            renderData();
        }
    }, [props.profileId])

    const renderData = async () => {
        setLoading(true);
        try {
            if(props.profileId != 0) {
                await renderUserAnalysisData();
                await renderUserData();
            }
        } catch (err) {
            errorHandler.handleError(err);
        } finally {
            setLoading(false);
        }
    }

    const renderUserData = async() => {
        try {
            let userObj = await userInfoService.getUserById(props.profileId);
            userObj = JSON.parse(userObj.body);
            setUser(userObj);
            setPhoto(!userObj.photo || userObj.photo === 'null' ? '' : userObj.photo);
            setImageUrl(!userObj.photo || userObj.photo === 'null' ? '' : URL.createObjectURL(dataURLtoFile(userObj.photo)));
            setUserAnalysisLoader(false);
        } catch (err) {
            errorHandler.handleError(err);
        }
    }

    const renderUserAnalysisData = async() => {
        try {
            let userAnalysisObj = await userAnalysisService.getUserAnalysisByAssigneeUserId(props.profileId);
            userAnalysisObj = JSON.parse(userAnalysisObj.body);
            setUserAnalysis(userAnalysisObj);
            setUserAnalysisLoader(false);
        } catch (err) {
            errorHandler.handleError(err);
        }
    }

    const dataURLtoFile = (dataurl) => {
        var arr = dataurl.split(','),
            mime = arr[0].match(/:(.*?);/)[1],
            bstr = atob(arr[1]),
            n = bstr.length,
            u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], {type: mime});
    }

    return (
        <Modal
            open={props.open}
            onClose={props.closePopup}>
            <div className={className}>
                <div className={`${className}__container`}>
                    {/* Header */}
                    <div className={`${className}__header`}>
                        <div className={`${className}__header__left`}>
                            <PersonIcon className={`${className}__header__icon`} />
                            <h2 className={`${className}__header__title`}>
                                {props?.profileId === 0 ? 'Unassigned' : 'User Profile'}
                            </h2>
                        </div>
                        <button 
                            className={`${className}__header__close`} 
                            onClick={props.closePopup}>
                            <Close />
                        </button>
                    </div>

                    {loading && props?.profileId !== 0 ? (
                        <div className={`${className}__loading`}>
                            <CircularProgress size={40} />
                            <p>Loading profile...</p>
                        </div>
                    ) : props?.profileId === 0 ? (
                        /* Unassigned View */
                        <div className={`${className}__unassigned`}>
                            <div className={`${className}__unassigned__avatar`}>
                                <ProfilePicture />
                            </div>
                            <h3 className={`${className}__unassigned__title`}>Unassigned</h3>
                            <p className={`${className}__unassigned__text`}>
                                This item is currently unassigned
                            </p>
                        </div>
                    ) : (
                        /* Profile Content */
                        <div className={`${className}__content`}>
                            {isMobile ? (
                                /* Mobile Layout - Vertical Scroll */
                                <div className={`${className}__mobile`}>
                                    {/* Profile Header Card */}
                                    <div className={`${className}__mobile__profile`}>
                                        <div className={`${className}__mobile__profile__avatar`}>
                                            {imageUrl.trim() === '' ? (
                                                <ProfilePicture />
                                            ) : (
                                                <img src={imageUrl} alt='Profile' />
                                            )}
                                        </div>
                                        <div className={`${className}__mobile__profile__info`}>
                                            <div className={`${className}__mobile__profile__name`}>
                                                {user?.fullName}
                                            </div>
                                            <div className={`${className}__mobile__profile__username`}>
                                                @{user?.userName}
                                            </div>
                                            {user?.designation && (
                                                <div className={`${className}__mobile__profile__meta`}>
                                                    <WorkIcon />
                                                    <span>{user?.designation}</span>
                                                </div>
                                            )}
                                            {user?.email && (
                                                <div className={`${className}__mobile__profile__meta`}>
                                                    <EmailIcon />
                                                    <span>{user?.email}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Performance Cards */}
                                    <div className={`${className}__mobile__section`}>
                                        <ProfileUserPerformance
                                            userAnalysis={userAnalysis}
                                            userAnalysisLoader={userAnalysisLoader}
                                        />
                                    </div>

                                    {/* Statistics */}
                                    <div className={`${className}__mobile__section`}>
                                        <ProfileStatistics
                                            userAnalysis={userAnalysis}
                                            userAnalysisLoader={userAnalysisLoader}
                                            aspectRatio={1}
                                        />
                                    </div>

                                    {/* User Charts */}
                                    <div className={`${className}__mobile__section`}>
                                        <ProfileUserCharts
                                            userAnalysis={userAnalysis}
                                            userAnalysisLoader={userAnalysisLoader}
                                        />
                                    </div>
                                </div>
                            ) : (
                                /* Desktop Layout - Grid */
                                <Grid container spacing={3}>
                                    {/* Left Sidebar - Profile Info */}
                                    <Grid item xs={12} lg={3}>
                                        <div className={`${className}__sidebar`}>
                                            {/* Profile Card */}
                                            <div className={`${className}__profile`}>
                                                <div className={`${className}__profile__avatar`}>
                                                    {imageUrl.trim() === '' ? (
                                                        <ProfilePicture />
                                                    ) : (
                                                        <img src={imageUrl} alt='Profile' />
                                                    )}
                                                </div>
                                                <div className={`${className}__profile__name`}>
                                                    {user?.fullName}
                                                </div>
                                                <div className={`${className}__profile__username`}>
                                                    @{user?.userName}
                                                </div>
                                                {user?.designation && (
                                                    <div className={`${className}__profile__designation`}>
                                                        <WorkIcon />
                                                        <span>{user?.designation}</span>
                                                    </div>
                                                )}
                                                {user?.email && (
                                                    <div className={`${className}__profile__email`}>
                                                        <EmailIcon />
                                                        <span>{user?.email}</span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Performance Card */}
                                            <ProfileUserPerformance
                                                userAnalysis={userAnalysis}
                                                userAnalysisLoader={userAnalysisLoader}
                                            />
                                        </div>
                                    </Grid>

                                    {/* Main Content */}
                                    <Grid item xs={12} lg={9}>
                                        <div className={`${className}__main`}>

                                            {/* Charts and Statistics */}
                                            <Grid container spacing={3}>
                                                <Grid item xs={12} md={6}>
                                                    <ProfileStatistics
                                                        userAnalysis={userAnalysis}
                                                        userAnalysisLoader={userAnalysisLoader}
                                                        aspectRatio={1}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <ProfileUserCharts
                                                        userAnalysis={userAnalysis}
                                                        userAnalysisLoader={userAnalysisLoader}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </div>
                                    </Grid>
                                </Grid>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
}
